import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Tag, Collapse, Progress } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { AiValidationResponse, AiValidationIssue } from 'types/legalFormality'

const SEVERITY_CONFIG = {
  critical: { color: 'red', icon: <CloseCircleOutlined /> },
  warning: { color: 'orange', icon: <WarningOutlined /> },
  info: { color: 'blue', icon: <InfoCircleOutlined /> },
}

const AiValidationResult = () => {
  const { t } = useTranslation()
  const { legalFormality } = useContext(CurrentLegalFormalityContext)

  const validation: AiValidationResponse | null = useMemo(() => {
    if (!legalFormality?.aiValidationResponse) return null
    try {
      return JSON.parse(legalFormality.aiValidationResponse)
    } catch {
      return null
    }
  }, [legalFormality?.aiValidationResponse])

  if (!validation) return null

  const scoreColor =
    validation.score >= 80
      ? '#52c41a'
      : validation.score >= 50
      ? '#faad14'
      : '#ff4d4f'

  return (
    <Container>
      <Header>
        {validation.compliant ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('legalFormalities:show.misc.aiValidation.compliant', {
              defaultValue: 'Conforme',
            })}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            {t('legalFormalities:show.misc.aiValidation.nonCompliant', {
              defaultValue: 'Non conforme',
            })}
          </Tag>
        )}
        <Progress
          type="circle"
          percent={validation.score}
          size="small"
          strokeColor={scoreColor}
        />
      </Header>

      <Summary>{validation.summary}</Summary>

      {(validation.issues ?? []).length > 0 && (
        <Collapse>
          <Collapse.Panel
            key="issues"
            header={t('legalFormalities:show.misc.aiValidation.issues', {
              defaultValue: 'Problèmes détectés',
              count: validation.issues.length,
            })}
          >
            <IssuesList>
              {(validation.issues ?? []).map(
                (issue: AiValidationIssue, idx: number) => {
                  const config = SEVERITY_CONFIG[issue.severity]
                  return (
                    <IssueItem key={idx}>
                      <Tag icon={config.icon} color={config.color}>
                        {issue.field}
                      </Tag>
                      <IssueMessage>{issue.message}</IssueMessage>
                    </IssueItem>
                  )
                }
              )}
            </IssuesList>
          </Collapse.Panel>
        </Collapse>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Summary = styled.div`
  font-size: 13px;
  color: #595959;
  line-height: 1.5;
`

const IssuesList = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
`

const IssueItem = styled.div`
  display: flex;
  flex-flow: column;
  gap: 4px;
`

const IssueMessage = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  padding-left: 4px;
`

export default AiValidationResult
